import {
  Component,
  Directive,
  Output,
  EventEmitter,
  HostListener,
  Input,
  ElementRef,
  Renderer2,
  OnInit,
} from "@angular/core";
import { ZpDataService } from "./services/zp-data.service";
import { UserInfoService } from "./services/user-info.service";
import { Subscription } from "rxjs";
import { IUser } from "./models/user.model";
import { UserIdleService } from "angular-user-idle";
import moment from "moment";
import { NavigationStart, Router, RouterOutlet } from "@angular/router";
import { ThemeService } from "./theme/theme-service.service";
import { DeviceDetectorService } from "ngx-device-detector";

export let browserRefresh = false;

@Component({
    selector: "app-root",
    templateUrl: "./app.component.html",
    styleUrls: ["./app.component.scss"],
    standalone: false
})
export class AppComponent {
  @Input() firstPara = "Your session will expire soon.";
  @Input() scdPara = "Do you want to stay connected?";
  public title = "zp-fe";
  public myUser: IUser;
  public loggedIn = false;
  public showModal = false;
  public subscription: Subscription;
  public subscription2: Subscription;
  readonly googlePlayLink: string;
  readonly appStoreLink: string;
  public count: number;
  public now: moment.Moment;
  public lastWeek: string;
  public lastYear: string;
  public lastMonth: string;
  public lastQuarter: string;
  public showInstallBanner = false;
  public showIosBanner = false;
  private installPromptEvent: any;

  constructor(
    private ZpDataService: ZpDataService,
    private router: Router,
    private userInfo: UserInfoService,
    private userIdle: UserIdleService,
    public themeService: ThemeService,
    private deviceDetector: DeviceDetectorService
  ) {
    this.now = moment();
    this.lastWeek = moment().subtract(1, "weeks").format("YYYY-MM-DD");
    this.lastMonth = moment().subtract(1, "months").format("YYYY-MM-DD");
    this.lastQuarter = moment().subtract(3, "months").format("YYYY-MM-DD");
    this.lastYear = moment().subtract(1, "years").format("YYYY-MM-DD");
    this.subscription2 = router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        browserRefresh = !router.navigated;
      }
    });
  }

  @HostListener('window:beforeinstallprompt', ['$event'])
  onBeforeInstallPrompt(event: any) {
    event.preventDefault();
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isMobile = this.deviceDetector.isMobile() || this.deviceDetector.isTablet();
    if (!dismissed && !isStandalone && isMobile) {
      this.installPromptEvent = event;
      this.showInstallBanner = true;
    }
  }

  async installApp() {
    if (!this.installPromptEvent) return;
    this.installPromptEvent.prompt();
    const { outcome } = await this.installPromptEvent.userChoice;
    if (outcome === 'accepted') {
      this.showInstallBanner = false;
    }
    this.installPromptEvent = null;
  }

  dismissInstallBanner() {
    this.showInstallBanner = false;
    localStorage.setItem('pwa-install-dismissed', '1');
  }

  dismissIosBanner() {
    this.showIosBanner = false;
    localStorage.setItem('pwa-ios-dismissed', '1');
  }

  ngOnInit() {
    const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent) && 'standalone' in navigator;
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const dismissed = localStorage.getItem('pwa-ios-dismissed');
    if (isIos && !isStandalone && !dismissed) {
      this.showIosBanner = true;
    }

    this.subscription = this.userInfo.getMessage().subscribe((user) => {
      if (user && user != "null" && user.length > 0) {
        this.loggedIn = true;
      } else {
        this.loggedIn = false;
      }
      try {
        this.myUser = JSON.parse(user);
      } catch {}
    });
    // Idle
    this.userIdle.startWatching();
    this.userIdle.onTimerStart().subscribe((count) => {
      this.showModal = true;
    });
    this.userIdle.onTimeout().subscribe(() => {
      this.ZpDataService.logout();
      this.ZpDataService.alertMessage({
        message: "Session Expired",
        additionalInfo: "You have been logged out for your security.",
      });
      this.stopWatching();
    });
    
  }

  

  defaultTheme() {
    this.themeService.setDefaultTheme();
  }

  blueTheme() {
    this.themeService.setBlueTheme();
  }

  onTimeout() {
    this.ZpDataService.logout();
    this.ZpDataService.alertMessage({
      message: "Session Expired",
      additionalInfo: "You have been logged out for your security.",
    });
    this.stopWatching();
  }

  stopTimer() {
    this.userIdle.stopTimer();
    this.showModal = false;
  }

  stopWatching() {
    this.userIdle.stopWatching();
    this.showModal = false;
  }

  startWatching() {
    this.userIdle.startWatching();
  }

  resetTimer() {
    this.userIdle.resetTimer();
  }

  async showLogoutModal(choice) {
    if (choice === "No") {
      this.ZpDataService.logout();
      this.ZpDataService.alertMessage({
        message: "Session Expired",
        additionalInfo: "Please log in again.",
      });
      this.stopWatching();
    }
    if (choice === "Yes") {
      this.stopTimer();
      this.resetTimer();
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.subscription2.unsubscribe();
  }
}

// CapsLock
@Directive({
    selector: "[capsLock]",
    standalone: false
})
export class TrackCapsDirective {
  @Output("capsLock") capsLock = new EventEmitter<Boolean>();

  @HostListener("window:keydown", ["$event"])
  onKeyDown(event: KeyboardEvent): void {
    this.capsLock.emit(
      event.getModifierState && event.getModifierState("CapsLock")
    );
  }
  @HostListener("window:keyup", ["$event"])
  onKeyUp(event: KeyboardEvent): void {
    this.capsLock.emit(
      event.getModifierState && event.getModifierState("CapsLock")
    );
  }
}

// Click Outside
@Directive({
    selector: "[clickOutside]",
    standalone: false
})
export class ClickOutsideDirective {
  @Output() clickOutside = new EventEmitter<void>();

  constructor(private elementRef: ElementRef) {}

  @HostListener("document:click", ["$event.target"])
  public onClick(target) {
    const clickedInside = this.elementRef.nativeElement.contains(target);
    if (!clickedInside) {
      this.clickOutside.emit();
    }
  }
}
